import { isMajorGenre, isMinorGenre } from '~/features/facets/types'
import type { Genre, MajorGenre, MinorGenre } from '~/features/facets/types'
import type { GenreCode } from '~/lib/graphql/scalars'
import type { SearchCriteria } from '../validate'
import type { Facet } from './type'

export function genreCodeToFacet(code: string, map: Map<string, Genre>): Facet | undefined {
  const genre = map.get(code)
  if (genre === undefined) {
    return undefined
  }
  if (isMajorGenre(genre)) {
    return majorGenreToFacet(genre)
  }
  if (isMinorGenre(genre)) {
    const parent = map.get(genre.major_code)
    if (parent === undefined || !isMajorGenre(parent)) {
      return undefined
    }
    return minorGenreToFacet(genre, parent)
  }
  return undefined
}

export function majorGenreToFacet(genre: MajorGenre): Facet {
  return {
    facetKey: `genre.${genre.code}`,
    ...genreCommonFacet(genre),
    add(current) {
      const { genreCodes, ...rest } = current ?? {}
      const codes = new Set(genreCodes)
      codes.add(genre.code)
      for (const minor of genre.minors) {
        codes.add(minor.code)
      }
      return {
        ...rest,
        genreCodes: Array.from(codes).toSorted(),
      }
    },
    remove(current) {
      const { genreCodes, ...rest } = current ?? {}
      const codes = new Set(genreCodes)
      codes.delete(genre.code)
      for (const minor of genre.minors) {
        codes.delete(minor.code)
      }
      if (codes.size === 0) {
        return rest
      }
      return {
        ...rest,
        genreCodes: Array.from(codes).toSorted(),
      }
    },
    showInList(criteria: SearchCriteria | undefined): boolean {
      return this.selected(criteria)
    },
  }
}

export function minorGenreToFacet(genre: MinorGenre, parent: MajorGenre): Facet {
  return {
    facetKey: `genre.${parent.code}.${genre.code}`,
    ...genreCommonFacet(genre),
    add(current) {
      const { genreCodes, ...rest } = current ?? {}
      const codes = new Set(genreCodes)
      codes.add(genre.code as GenreCode)
      // 自身を追加した結果、同じ親ジャンルに所属する子ジャンルが揃ったら、親ジャンルも選択状態に
      // 「フランス料理」を選んで「洋食」が全部そろったら「洋食」もONになる
      const completed = parent.minors.every((minor) => codes.has(minor.code))
      if (completed) {
        codes.add(parent.code)
      }
      return {
        ...rest,
        genreCodes: Array.from(codes).toSorted(),
      }
    },
    remove(current) {
      const { genreCodes, ...rest } = current ?? {}
      const codes = new Set(genreCodes)
      codes.delete(genre.code)
      codes.delete(parent.code)
      if (codes.size === 0) {
        return rest
      }
      return {
        ...rest,
        genreCodes: Array.from(codes).toSorted(),
      }
    },
    showInList(criteria: SearchCriteria | undefined): boolean {
      const codes = new Set(criteria?.genreCodes)
      const completed = parent.minors.every((minor) => codes.has(minor.code))
      return !completed
    },
  }
}

function genreCommonFacet(genre: Genre): Pick<Facet, 'label' | 'selected'> {
  return {
    label: genre.name,
    selected(criteria) {
      return criteria?.genreCodes?.includes(genre.code) ?? false
    },
  }
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  const minor: MinorGenre = {
    code: '10001' as GenreCode,
    major_code: '10000' as GenreCode,
    name: 'フランス料理',
    display_order: 1,
    tag_id: 46,
  }
  const major: MajorGenre = {
    code: '10000' as GenreCode,
    name: '洋食',
    display_order: 1,
    tag_id: 6,
    minors: [
      minor,
      {
        code: '10002' as GenreCode,
        major_code: '10000' as GenreCode,
        name: 'イタリア料理',
        display_order: 2,
        tag_id: 47,
      },
      {
        code: '10004' as GenreCode,
        major_code: '10000' as GenreCode,
        name: 'スペイン料理',
        display_order: 3,
        tag_id: 48,
      },
      {
        code: '10008' as GenreCode,
        major_code: '10000' as GenreCode,
        name: 'ステーキ・グリル料理',
        display_order: 4,
        tag_id: 49,
      },
      {
        code: '10999' as GenreCode,
        major_code: '10000' as GenreCode,
        name: '洋食その他',
        display_order: 5,
        tag_id: 54,
      },
    ],
  }

  describe('MajorGenreFacet', () => {
    const facet = majorGenreToFacet(major)

    it('facetKey', () => {
      expect(facet.facetKey).toBe('genre.10000')
    })

    describe('selected', () => {
      it('未指定状態', () => {
        expect(facet.selected(undefined)).toBe(false)
      })
      it('指定状態', () => {
        expect(facet.selected({ genreCodes: ['10000', '10001'] as GenreCode[] })).toBe(true)
      })
      it('補完のジャンルが指定されている', () => {
        expect(facet.selected({ genreCodes: ['10001'] as GenreCode[] })).toBe(false)
      })
    })

    describe('add', () => {
      it('子供も含めて全部追加される', () => {
        const current: SearchCriteria = {}
        const next = facet.add(current)
        expect(next.genreCodes).toStrictEqual([
          '10000',
          '10001',
          '10002',
          '10004',
          '10008',
          '10999',
        ])
      })

      it('重複は排除', () => {
        const current: SearchCriteria = {
          genreCodes: ['10001', '10002'] as GenreCode[],
        }
        const next = facet.add(current)
        expect(next.genreCodes).toStrictEqual([
          '10000',
          '10001',
          '10002',
          '10004',
          '10008',
          '10999',
        ])
      })

      it('選択状態のときは何も変わらない', () => {
        const current: SearchCriteria = {
          genreCodes: [
            '10000',
            '10001',
            '10002',
            '10004',
            '10008',
            '10999',
            '20001',
          ] as GenreCode[],
        }
        const next = facet.add(current)
        expect(next.genreCodes).toStrictEqual([
          '10000',
          '10001',
          '10002',
          '10004',
          '10008',
          '10999',
          '20001',
        ])
      })

      it('追加した時点でソート', () => {
        const current: SearchCriteria = {
          genreCodes: ['20001'] as GenreCode[],
        }
        const next = facet.add(current)
        expect(next.genreCodes).toStrictEqual([
          '10000',
          '10001',
          '10002',
          '10004',
          '10008',
          '10999',
          '20001',
        ])
      })
    })

    describe('remove', () => {
      it('親ジャンルを除外するときは子も全部消える', () => {
        const genreCodes = [
          '10000',
          '10001',
          '10002',
          '10004',
          '10008',
          '10999',
          '20000',
          '20001',
        ] as GenreCode[]
        const result = facet.remove({ genreCodes })
        expect(result).toStrictEqual({ genreCodes: ['20000', '20001'] })
      })

      it('除外した結果、空になったときはキーがなくなる', () => {
        const genreCodes = ['10000', '10001', '10002', '10004', '10008', '10999'] as GenreCode[]
        const result = facet.remove({ genreCodes })
        expect(result).toStrictEqual({})
      })
    })
  })

  describe('MinorGenreFacet', () => {
    const facet = minorGenreToFacet(minor, major)

    it('facetKey', () => {
      expect(facet.facetKey).toBe('genre.10000.10001')
    })

    describe('selected', () => {
      it('未指定状態', () => {
        expect(facet.selected(undefined)).toBe(false)
      })
      it('指定状態', () => {
        expect(facet.selected({ genreCodes: ['10000', '10001'] as GenreCode[] })).toBe(true)
      })
      it('他のジャンルが指定されている', () => {
        expect(facet.selected({ genreCodes: ['10000'] as GenreCode[] })).toBe(false)
      })
    })

    describe('add', () => {
      it('自分だけが追加される', () => {
        const current: SearchCriteria = {}
        const next = facet.add(current)
        expect(next.genreCodes).toStrictEqual(['10001'])
      })

      it('同じ親に属する子ジャンルが揃うと親も追加される', () => {
        const genreCodes = ['10002', '10004', '10008', '10999', '20000', '20001'] as GenreCode[]
        const next = facet.add({ genreCodes })
        expect(next.genreCodes).toStrictEqual([
          '10000',
          '10001',
          '10002',
          '10004',
          '10008',
          '10999',
          '20000',
          '20001',
        ])
      })

      it('選択状態のときは何も変わらない', () => {
        const current: SearchCriteria = {
          genreCodes: ['10001', '10002'] as GenreCode[],
        }
        const next = facet.add(current)
        expect(next.genreCodes).toStrictEqual(['10001', '10002'])
      })

      it('追加した時点でソート', () => {
        const current: SearchCriteria = {
          genreCodes: ['20001'] as GenreCode[],
        }
        const next = facet.add(current)
        expect(next.genreCodes).toStrictEqual(['10001', '20001'])
      })
    })

    describe('remove', () => {
      it('子ジャンルが外れると親も消える', () => {
        const genreCodes = [
          '10000',
          '10001',
          '10002',
          '10004',
          '10008',
          '10999',
          '20000',
          '20001',
        ] as GenreCode[]
        const result = facet.remove({ genreCodes })
        expect(result).toStrictEqual({
          genreCodes: ['10002', '10004', '10008', '10999', '20000', '20001'],
        })
      })

      it('除外した結果、空になったときはキーがなくなる', () => {
        const genreCodes = ['10001'] as GenreCode[]
        const result = facet.remove({ genreCodes })
        expect(result).toStrictEqual({})
      })
    })
  })

  const genres = [major, ...(major.minors as Genre[])] as Genre[]
  const genreMap = new Map<string, Genre>(genres.map((genre) => [genre.code, genre]))

  describe('normalizeGenres', () => {
    it('全部選択されているジャンルは親ジャンルにまとめる', () => {
      const genreCodes = ['10000', '10001', '10002', '10004', '10008', '10999'] as GenreCode[]
      const criteria: SearchCriteria = { genreCodes }
      const facets = genreCodes
        .map((code) => genreCodeToFacet(code, genreMap))
        .filter((g) => g !== undefined)
        .filter((g) => g.showInList(criteria))
      expect(facets.map((g) => g.facetKey.split('.').at(-1))).toStrictEqual(['10000'])
    })
  })
}
